import React, { useEffect, useRef, useState } from 'react';

import { Button, Flex, Space, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import TwoStepsUpload from '../components/BasicComponents/TwoStepsUpload';
import useNotification from '@/hooks/notifications';
import useIbmS3Uploader from '../hooks/useIbmS3UploaderNew';
import { getSetting } from '../utils/settings';
import InfoTooltip from '../components/BasicComponents/InfoTooltip';

const { Title } = Typography;

export const DictionaryTypes = {
	SYNONYMS: 'Synonyms',
	HYPONYMS: 'Hyponyms',
	ACRONYMS: 'Acronyms',
	KEYWORDS_EXPANSION: 'KeywordsExpansion',
	ALIASES: 'Aliases',
	AUTOCORRECTION_WORDS: 'AutocorrectionWords'
};

const TypesData = {
	[DictionaryTypes.SYNONYMS]: {
		name: 'Synonyms',
		fileName: 'EP_MMC_Synonyms.xlsx',
		bucketPrefix: 'CMS/Synonyms/',
		downloadUrl: "https://cos-ep-mp-multimedia-test.s3.eu-de.cloud-object-storage.appdomain.cloud/CMS/Synonyms/EP_MMC_Synonyms.xlsx"
	},
	[DictionaryTypes.HYPONYMS]: {
		name: 'Hyponyms',
		fileName: 'EP_MMC_Hyponyms.xlsx',
		bucketPrefix: 'CMS/Hyponyms/',
		downloadUrl: "https://cos-ep-mp-multimedia-test.s3.eu-de.cloud-object-storage.appdomain.cloud/CMS/Hyponyms/EP_MMC_Hyponyms.xlsx"
	},
	[DictionaryTypes.ACRONYMS]: {
		name: 'Acronyms',
		fileName: 'EP_MMC_Acronyms.xlsx',
		bucketPrefix: 'CMS/Acronyms/',
		downloadUrl: "https://cos-ep-mp-multimedia-test.s3.eu-de.cloud-object-storage.appdomain.cloud/CMS/Acronyms/EP_MMC_Acronyms.xlsx"
	},
	[DictionaryTypes.ALIASES]: {
		name: 'Aliases',
		fileName: 'EP_MMC_Aliases.xlsx',
		bucketPrefix: 'CMS/Aliases/',
		downloadUrl: "https://cos-ep-mp-multimedia-test.s3.eu-de.cloud-object-storage.appdomain.cloud/CMS/Aliases/EP_MMC_Aliases.xlsx"
	},
	[DictionaryTypes.KEYWORDS_EXPANSION]: {
		name: 'Keywords Expansion',
		fileName: 'EP_MMC_KeywordsExpansion.xlsx',
		bucketPrefix: 'CMS/Keywords-expansion/',
		downloadUrl: "https://cos-ep-mp-multimedia-test.s3.eu-de.cloud-object-storage.appdomain.cloud/CMS/Keywords-expansion/EP_MMC_KeywordsExpansion.xlsx"
	},
	[DictionaryTypes.AUTOCORRECTION_WORDS]: {
		name: 'Autocorrection Words',
		fileName: 'EP_MMC_AutocorrectionWords.xlsx',
		bucketPrefix: 'CMS/Autocorrection-words/',
		downloadUrl: "https://cos-ep-mp-multimedia-test.s3.eu-de.cloud-object-storage.appdomain.cloud/CMS/Autocorrection-words/EP_MMC_AutocorrectionWords.xlsx"
	}
};

export default function Dictionary ({type}) {
	const [fileList, setFileList] = useState([]);
	const { openNotification } = useNotification();

	const useIbmS3UploaderRef = useRef(
			useIbmS3Uploader({
				setFile: (file) => setFileList([file]),
				bucketPrefix: TypesData[type].bucketPrefix,
				bucketName: null,
				endpoint: getSetting('S3_ENDPOINT'),
				cb: callback
			}
		)
	);
	const {handleUpload, updateBucketPrefix} = useIbmS3UploaderRef.current;

	useEffect(() => {
		updateBucketPrefix(TypesData[type].bucketPrefix);
	}, [type]);

	function checkName (file) {
		const notification = {message: 'Something failed!', description: 'The file name is wrong!', type: 'error'};
		const name = TypesData[type].fileName;

		if (file.name !== name) {
			openNotification(notification);
			return false;
		}

		return true;
	}

	function callback ({error, message}) {
		if (error) {
			openNotification({message: 'Something failed!', description: message, type: 'error'});
		} else {
			openNotification({message: 'Upload successful', description: message, type: 'success'});
		}
	}

	return (
		<Flex gap="small" align="flex-start" vertical style={{ width: '100%' }}>
			<Title>{`Multilingual Dictionaries > ${TypesData[type].name}`}</Title>
			<Title level={2}>Previous versions</Title>
			<Button
				type="primary"
				icon={<DownloadOutlined />}
				style={{ marginTop: 10 }}
				href={TypesData[type].downloadUrl}
				target="_blank"
				rel="noreferrer noopener"
			>
				Download
			</Button>
			<Space justify="center" style={{ width: '100%' }}>
				<Title level={2} size="large">Upload new version</Title>
				<InfoTooltip text={`To upload a new version, the file must have the same filename as the downloaded file. For example: "${TypesData[type].fileName}"`} />
			</Space>
			<TwoStepsUpload
				fileList={fileList}
				setFileList={setFileList}
				onUpload={() => handleUpload({file: fileList[0]})}
				onRemove={() => setFileList([])}
				beforeUpload={checkName}
				accept=".xlsx"
				style={{ width: '100%' }}
			/>
		</Flex>
	);
}